import React, { useEffect, useState } from "react"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import withRouter from "Components/Common/withRouter"
import DataTableTemplate from "Components/templates/data-table-template"
import ActionButton from "Components/atoms/buttons/action-button"
import { PlusIcon } from "Components/atoms/icons"
import SearchSelect from "Components/molecule/inputs/search-select"
import BudgetFormModal from "Components/organisms/expense-budget/budget-form-modal"
import {
    ExpenseBudget, ExpenseBudgetPayload, ExpenseBudgetSearchFields, employeeName,
} from "models/expense-budget"
import { createExpenseBudget, getExpenseBudgetSearchFields, getExpenseBudgets } from "slices/finance/expense-budget/thunk"
import { navigateToExpenseBudgetDetails } from "utils/navigator"
import { formatCurrency } from "utils/tools"

// No pagination: load every budget for the fiscal year (tables sort by clicking a column title)
const ALL_ROWS = Number.MAX_SAFE_INTEGER

const ExpenseBudgetsPage = (props: any) => {
    document.title = "Expense Budgets | IbexIQ"
    const [budgets, setBudgets] = useState<ExpenseBudget[]>([])
    const [totalBudgets, setTotalBudgets] = useState(0)
    const [searchFields, setSearchFields] = useState<ExpenseBudgetSearchFields | null>(null)
    const [fiscalYearId, setFiscalYearId] = useState<number | undefined>(undefined)
    const [isLoading, setIsLoading] = useState(true)
    const [isFormOpen, setIsFormOpen] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        getExpenseBudgetSearchFields().then(setSearchFields)
    }, [])

    useEffect(() => {
        const fetchBudgets = async () => {
            setIsLoading(true)
            const result = await getExpenseBudgets({ page: 0, limit: ALL_ROWS, fiscalYearId })
            setBudgets(result?.data ?? [])
            setTotalBudgets(result?.count ?? 0)
            setIsLoading(false)
        }
        fetchBudgets()
    }, [fiscalYearId])

    const handleOpenBudget = (budget: ExpenseBudget) => navigateToExpenseBudgetDetails(props.router.navigate, budget.id)

    const handleCreateBudget = async (payload: ExpenseBudgetPayload) => {
        setIsSaving(true)
        const budget = await createExpenseBudget(payload)
        setIsSaving(false)
        if (budget) navigateToExpenseBudgetDetails(props.router.navigate, budget.id)
    }

    const columns = React.useMemo<ColumnDef<ExpenseBudget>[]>(() => [
        {
            header: "Department",
            id: "department",
            accessorFn: (row) => `${row.department.code} ${row.department.description}`,
            cell: ({ row }) => (
                <a style={{ color: "var(--bs-table-link-color)", cursor: "pointer" }} onClick={() => handleOpenBudget(row.original)}>
                    {row.original.department.code} — {row.original.department.description}
                </a>
            ),
        },
        { header: "Fiscal Year", id: "fiscalYear", accessorFn: (row) => row.fiscalYearId, size: 80, cell: ({ row }) => `FY${row.original.fiscalYearId}` },
        { header: "Owner", id: "owner", accessorFn: (row) => employeeName(row.owner), cell: ({ row }) => employeeName(row.original.owner) },
        {
            header: "Cost Codes",
            id: "costCodes",
            accessorFn: (row) => row.costCodes.map(c => c.transactionAccount.code.trim()).join(", "),
            cell: ({ row }) => row.original.costCodes.map(c => c.transactionAccount.code.trim()).join(", ") || "-",
        },
        {
            header: "Approved",
            id: "approvedAmount",
            accessorFn: (row) => Number(row.approvedAmount),
            meta: { getCellStyle: () => ({ textAlign: "right" }), getHeaderStyle: () => ({ textAlign: "right" }) },
            cell: ({ row }) => formatCurrency(row.original.approvedAmount),
        },
    ], [])

    const table = useDataTable({ data: budgets, columns, getRowId: row => row.id })

    const yearOptions = [
        { value: "", label: "All years" },
        ...(searchFields?.fiscalYears ?? []).map(y => ({ value: String(y.id), label: `FY${y.id}` })),
    ]

    return (
        <DataTableTemplate
            title="Finance"
            breadcrumbItem="Expense Budgets"
            actions={<ActionButton onClick={() => setIsFormOpen(true)}>New Budget <PlusIcon /></ActionButton>}
            tableActions={
                <SearchSelect
                    value={String(fiscalYearId ?? "")}
                    onChange={option => setFiscalYearId(option?.value ? Number(option.value) : undefined)}
                    options={yearOptions}
                    inputStyle={{ margin: 0, minWidth: "150px" }}
                />
            }
            modal={
                <BudgetFormModal
                    isOpen={isFormOpen}
                    searchFields={searchFields}
                    isSaving={isSaving}
                    onClose={() => setIsFormOpen(false)}
                    onSave={handleCreateBudget}
                />
            }
            table={table}
            columns={columns}
            isLoading={isLoading}
            loadingTitle="Loading Expense Budgets"
            rowCount={totalBudgets}
        />
    )
}

export default withRouter(ExpenseBudgetsPage)
